<script setup lang="ts">
import type { Product, Category, ProductLine } from '~/types'

// SEO
useHead({
  title: 'Catálogo de Productos - Granados Pinturas Industriales',
  meta: [
    { name: 'description', content: 'Explora nuestro catálogo de pinturas industriales Axalta. Primarios, acabados, selladores y más.' }
  ]
})

// Composables
const { fetchProducts } = useProducts()
const { fetchCategories, fetchProductLines, fetchComponentTypes } = useCategories()
const { filters } = useFilters()

// State
const products = ref<Product[]>([])
const categories = ref<Category[]>([])
const productLines = ref<ProductLine[]>([])
const componentTypes = ref<string[]>([])
const loading = ref(true)
const filterOptionsLoading = ref(true)
const currentPage = ref(1)
const totalPages = ref(1)
const totalProducts = ref(0)
const pageSize = 24

// Load filter options on mount (cached)
const loadFilterOptions = async () => {
  filterOptionsLoading.value = true
  try {
    const [cats, lines, types] = await Promise.all([
      fetchCategories(),
      fetchProductLines(),
      fetchComponentTypes()
    ])
    categories.value = cats
    productLines.value = lines
    componentTypes.value = types
  } catch (error) {
    console.error('Error loading filter options:', error)
  } finally {
    filterOptionsLoading.value = false
  }
}

// Load products with debounce protection
let loadProductsAbortController: AbortController | null = null

const loadProducts = async () => {
  // Cancel previous request
  if (loadProductsAbortController) {
    loadProductsAbortController.abort()
  }

  loadProductsAbortController = new AbortController()
  loading.value = true

  try {
    const response = await fetchProducts(filters, currentPage.value, pageSize)
    products.value = response.products
    totalPages.value = response.totalPages
    totalProducts.value = response.total
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      console.error('Error loading products:', error)
    }
  } finally {
    loading.value = false
    loadProductsAbortController = null
  }
}

// Handle filter changes
const handleFiltersChange = () => {
  currentPage.value = 1
  loadProducts()
}

// Handle page change
const handlePageChange = (page: number) => {
  currentPage.value = page
  loadProducts()
  // Scroll to top of products with smooth behavior
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

// Initial load
onMounted(async () => {
  // Load filter options first (can be parallel with products)
  await Promise.all([
    loadFilterOptions(),
    loadProducts()
  ])
})

// Watch filters for changes (from URL) - single watch instead of duplicate
watch(
  () => ({ ...filters }),
  () => {
    currentPage.value = 1
    loadProducts()
  },
  { deep: true }
)

// Cleanup on unmount
onBeforeUnmount(() => {
  if (loadProductsAbortController) {
    loadProductsAbortController.abort()
  }
})
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Header -->
    <div class="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
      <div class="container mx-auto px-4 py-8">
        <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Catálogo de Productos
        </h1>
        <p class="text-gray-600 dark:text-gray-400">
          Explora nuestra línea completa de pinturas industriales Axalta Coating Systems
        </p>
      </div>
    </div>

    <div class="container mx-auto px-4 py-8">
      <div class="flex flex-col lg:flex-row gap-8">
        <!-- Sidebar with filters -->
        <aside class="w-full lg:w-64 flex-shrink-0">
          <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 sticky top-4">
            <!-- Search -->
            <div class="mb-6">
              <CatalogProductSearch @search="handleFiltersChange" />
            </div>

            <!-- Filters -->
            <CatalogProductFilters
              :categories="categories"
              :product-lines="productLines"
              :component-types="componentTypes"
              @filters-change="handleFiltersChange"
            />
          </div>
        </aside>

        <!-- Main content -->
        <main class="flex-1 min-w-0">
          <!-- Results count -->
          <div class="flex items-center justify-between mb-6">
            <p class="text-sm text-gray-600 dark:text-gray-400">
              <span v-if="loading">Cargando productos...</span>
              <span v-else>
                {{ totalProducts }} producto{{ totalProducts !== 1 ? 's' : '' }} encontrado{{ totalProducts !== 1 ? 's' : '' }}
              </span>
            </p>
          </div>

          <!-- Products grid -->
          <CatalogProductGrid
            :products="products"
            :loading="loading"
          />

          <!-- Pagination -->
          <div v-if="!loading && totalPages > 1" class="mt-8 flex justify-center">
            <UPagination
              :model-value="currentPage"
              :total="totalProducts"
              :page-count="pageSize"
              show-edges
              @update:model-value="handlePageChange"
            />
          </div>
        </main>
      </div>
    </div>
  </div>
</template>
